import type { Dataset } from '@life/data';
import type { DrawMode, Epoch, Life } from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { ThemeToggle } from '../components/ThemeToggle';
import type { LoadError } from '../state/boot';
import type { Game } from '../state/game';
import { GAME_MODES, type Settings } from '../state/settings';

const EPOCHS: readonly Epoch[] = ['past', 'present', 'future', 'any'];
const DRAW_MODES: readonly DrawMode[] = ['births', 'population'];
const DURATIONS: readonly Settings['durationMin'][] = [30, 60, 90];

export interface StartScreenProps {
  game: Game;
  ds: Dataset;
  /** «Свидетельство» на полке: показать итог завершённой жизни (DeathScreen в режиме просмотра, Task 10). */
  onCertificate?: (life: Life) => void;
  /** Сохранение не прочитано (мусор или чужая версия): сообщение над настройками. */
  loadError?: LoadError | null;
}

/** Пустая карта жизни: 80 пунктирных клеток. */
function EmptyStrip() {
  const cells = Array.from({ length: 80 }, (_, i) => <i key={i} class="off" />);
  return <div class="strip" aria-hidden="true">{cells}</div>;
}

interface RadioGroupProps<V extends string | number> {
  name: string;
  legend: string;
  values: readonly V[];
  current: V;
  label: (v: V) => string;
  onChange: (v: V) => void;
}

function RadioGroup<V extends string | number>({ name, legend, values, current, label, onChange }: RadioGroupProps<V>) {
  return (
    <fieldset class="field">
      <legend>{legend}</legend>
      <div class="radios">
        {values.map((v) => (
          <label key={String(v)}>
            <input type="radio" name={name} value={String(v)} checked={current === v} onChange={() => onChange(v)} />
            {label(v)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function StartScreen({ game, ds, onCertificate, loadError = null }: StartScreenProps) {
  const settings = game.settings.value;
  const patch = (p: Partial<Settings>) => {
    game.settings.value = { ...game.settings.value, ...p };
  };
  const countries = ds.countries.slice().sort((a, b) => a.nameRu.localeCompare(b.nameRu, 'ru'));
  const countryName = (iso3: string) => countries.find((c) => c.iso3 === iso3)?.nameRu ?? iso3;
  const hasLife = game.life.value !== null;
  const shelf = game.shelf.value.slice().reverse();

  return (
    <main class="page">
      <header class="topbar">
        <b>{t('ui.app.title')}</b>
        <ThemeToggle game={game} />
      </header>

      <section>
        <div class="eyebrow">{t('ui.start.eyebrow')}</div>
        <h1 class="big">{t('ui.start.counter')}</h1>
        <p class="lead">{t('ui.start.lead')}</p>
        <EmptyStrip />
      </section>

      {loadError !== null && (
        <div class="alert warn" role="status">
          {t(`ui.start.load.${loadError}`)}
        </div>
      )}

      <section>
        <h2 class="h-section">{t('ui.start.settings')}</h2>
        <RadioGroup
          name="epoch"
          legend={t('ui.start.epoch')}
          values={EPOCHS}
          current={settings.epoch}
          label={(v) => t(`ui.epoch.${v}`)}
          onChange={(epoch) => patch({ epoch })}
        />
        <RadioGroup
          name="draw"
          legend={t('ui.start.draw')}
          values={DRAW_MODES}
          current={settings.drawMode}
          label={(v) => t(`ui.draw.${v}`)}
          onChange={(drawMode) => patch({ drawMode })}
        />
        {/* Режим (core-loop-v2 §7): ход — по умолчанию; часы и длительность сессии — только в «Наблюдать». */}
        <RadioGroup
          name="mode"
          legend={t('ui.start.mode')}
          values={GAME_MODES}
          current={settings.mode}
          label={(v) => t(`ui.mode.${v}`)}
          onChange={(mode) => patch({ mode })}
        />
        <p class="hint mode-hint">{t(`ui.mode.${settings.mode}.hint`)}</p>
        {settings.mode === 'observe' && (
          <RadioGroup
            name="duration"
            legend={t('ui.start.duration')}
            values={DURATIONS}
            current={settings.durationMin}
            label={(v) => t(`ui.duration.${v}`)}
            onChange={(durationMin) => patch({ durationMin })}
          />
        )}
        <div class="field">
          <div class="radios">
            <label>
              <input
                type="checkbox"
                checked={settings.skipHeavy}
                onChange={(e) => patch({ skipHeavy: (e.currentTarget as HTMLInputElement).checked })}
              />
              {t('ui.start.skipHeavy')}
            </label>
          </div>
          <p class="hint">{t('ui.start.skipHeavy.hint')}</p>
        </div>
        <div class="field">
          <div class="radios">
            <label>
              <input
                type="checkbox"
                checked={settings.music}
                onChange={(e) => patch({ music: (e.currentTarget as HTMLInputElement).checked })}
              />
              {t('ui.start.music')}
            </label>
          </div>
          <p class="hint">{t('ui.start.music.hint')}</p>
        </div>
        <div class="field">
          <label for="real-country">{t('ui.start.realCountry')}</label>
          <select
            id="real-country"
            value={settings.realCountry ?? ''}
            onChange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              patch({ realCountry: v === '' ? null : v });
            }}
          >
            <option value="">{t('ui.start.realCountry.none')}</option>
            {countries.map((c) => (
              <option key={c.iso3} value={c.iso3}>
                {c.nameRu}
              </option>
            ))}
          </select>
          <p class="hint">{t('ui.start.realCountry.hint')}</p>
        </div>
      </section>

      <section class="actions">
        <button type="button" class="btn primary block" onClick={() => game.born()}>
          {t('ui.start.born')}
        </button>
        {hasLife && (
          <button type="button" class="btn block" onClick={() => game.startLife()}>
            {t('ui.start.continue')}
          </button>
        )}
      </section>

      {shelf.length > 0 && (
        <section>
          <h2 class="h-section">{t('ui.start.shelf')}</h2>
          <ul class="shelf">
            {shelf.map((life) => (
              <li key={life.id}>
                <span>
                  {t('ui.start.shelf.item', {
                    name: life.name,
                    country: countryName(life.iso3),
                    age: life.age,
                    years: yearsWord(life.age),
                  })}
                </span>
                <button type="button" class="btn" onClick={() => onCertificate?.(life)}>
                  {t('ui.start.certificate')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
